import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import classNames from 'classnames';
import { ProductImageViewModel } from '@/types/api';
import { NavigationCarousel } from '@/features/carousel';
import { CarouselArrowType, DotsType } from '@/features/carousel/types';
import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider';
import { Modal } from '@/features/modal';
import { VideoPlayer } from '@/features/video-player';
import usePDPData from '@/app/(pdp)/_utils/use-pdp-data';
import { useVariantContextValues } from '@/app/(pdp)/context/use-variant-context-values';
import { PUBLIC_IS_BOT } from '@/app/_constants/search-engines';
import { handleImageError } from '@/utils/image-error';
import { useRouteHash } from '@/utils/use-route-hash';
import { useImageMagnifier } from './use-image-magnifier';
import { generateImageAlt } from '../utils/generate-image-alt';
import { useGallaryItems } from '../utils/use-gallary-items';
const GalleryPanel = dynamic(() => import('../gallery-panel/gallery-panel'), {
  ssr: false
});
interface Props {
  clickItemHandler?: (item: ProductImageViewModel) => void;
  onSlideChange?: (index: number) => void;
  activeIndex: number;
}
const GalleryCarousel = ({
  clickItemHandler,
  onSlideChange,
  activeIndex
}: Props) => {
  const {
    isMobile
  } = useMobileDetect();
  const [isCarouselStatic, setIsCarouselStatic] = useState(true);
  const {
    activeVariant
  } = useVariantContextValues('pdp');
  const {
    magnifier,
    magnifiedImage,
    magnifiedImageClassName,
    magnifiedImageStyle,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseMove
  } = useImageMagnifier({
    magnifiedImageHeight: 478,
    magnifiedImageWidth: 478,
    zoomLevel: 2.8
  });
  const searchParams = useSearchParams();
  const variantId = searchParams.get('atpId');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    data
  } = usePDPData();
  const {
    images,
    thumbnails,
    videos
  } = useGallaryItems('pdp');
  const {
    addHash,
    removeHash
  } = useRouteHash({
    hash: 'gallery',
    onHashRemoved: () => setIsModalOpen(false)
  });
  const totalGalleryItemsCount = thumbnails.length;
  const handleItemClick = (item: ProductImageViewModel) => {
    if (isMobile) {
      setIsModalOpen(true);
      addHash();
    }
    !!clickItemHandler && clickItemHandler(item);
  };
  const slideChangeHandler = (activeIndex: number) => {
    !!onSlideChange && onSlideChange(activeIndex);
  };
  const closeGalleryModalHandler = () => {
    setIsModalOpen(false);
    removeHash();
  };
  useEffect(() => {
    setIsCarouselStatic(false);
    setTimeout(() => {
      setIsCarouselStatic(true);
    }, 100);
  }, [variantId]);
  if (!images.length) return <div className='h-64 w-64 lg:h-[316px] lg:w-[316px]' />;
  const imageSize = isMobile ? 248 : 308;
  return <>
            <NavigationCarousel className={classNames(totalGalleryItemsCount === 1 ? 'mx-auto my-0 !w-fit lg:!w-full' : 'lg:w-[316px]')} onSlideChange={slideChangeHandler} activeIndex={activeIndex} staticChildren={isMobile ? false : isCarouselStatic} options={{
      perView: 1,
      spacing: 0,
      mode: 'snap',
      breakPoints: {
        desktop: {
          perView: 1,
          spacing: 0,
          drag: false
        },
        mobile: {
          perView: totalGalleryItemsCount === 1 ? 1 : 'auto',
          spacing: 0
        }
      }
    }} arrowType={!isMobile ? CarouselArrowType.PDP : undefined} dotsType={DotsType.PDP} data-sentry-element="NavigationCarousel" data-sentry-source-file="gallery-carousel.tsx">
                {images.map(({
        imageUrl,
        id
      }, index) => imageUrl && <figure onMouseEnter={e => handleMouseEnter(e, imageUrl)} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} key={id} role='button' onClick={() => handleItemClick({
        imageUrl,
        id
      })} aria-label='open-gallery-modal' className='keen-slider__slide !h-64 !min-h-[256px] !w-64 !min-w-[256px] overflow-visible p-1 lg:!h-[316px] lg:!min-h-80 lg:!w-[316px] lg:!min-w-full'>
                                <img src={`${imageUrl}?w=320`} alt={generateImageAlt(index, data, activeVariant || undefined)} onError={handleImageError} className='h-full w-full rounded shadow' loading={index !== 0 && !PUBLIC_IS_BOT ? 'lazy' : undefined} width={imageSize} height={imageSize} />
                                {magnifier}
                            </figure>)}
                {videos && videos.map(video => {
        return <div key={video.id} className='keen-slider__slide relative box-border flex !w-64 !min-w-[256px] items-center justify-center overflow-hidden p-1 lg:!w-[316px] lg:!min-w-[316px]'>
                                <VideoPlayer url={video.videoUrl || ''} thumbnail={video.thumbnailImageUrl || ''} className='h-full w-full overflow-hidden rounded shadow' />
                            </div>;
      })}
            </NavigationCarousel>

            {magnifiedImage && <div className={magnifiedImageClassName} style={magnifiedImageStyle}></div>}

            <Modal title={data?.nameFa || data?.nameEn || ''} isOpen={isModalOpen} handleClose={closeGalleryModalHandler} className='top-0 overflow-hidden' displayType='withTopBar' bodyClassName='px-0 pb-0 max-h-[calc(100dvh-56px)]' headClassName='max-w-[calc(100%_-_40px)]' data-sentry-element="Modal" data-sentry-source-file="gallery-carousel.tsx">
                <GalleryPanel data-sentry-element="GalleryPanel" data-sentry-source-file="gallery-carousel.tsx" />
            </Modal>
        </>;
};
export default GalleryCarousel;